import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  ExpandLess,
  ExpandMore,
  HourglassEmpty,
  Pending,
  Refresh,
  RemoveCircleOutline,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import { useMemo, useState } from "react";

import {
  PipelineRunLog,
  PipelineStepLog,
  PostSyncStepStatus,
} from "../../services/distributionService";
import { useDistributionPostSyncRuns } from "../../hooks/useDistributionPostSyncRuns";
import { TerminalLogTail } from "./TerminalLogTail";

const STEP_LABELS: Record<string, string> = {
  // Steps actuales (orquestador vigente, 13 pasos §7)
  "01_refresh_monthly_sales": "1. Refresh ventas mensuales",
  "01a_refresh_product_sales": "1a. Refresh ventas por producto",
  "01b_classify_store_profiles": "1b. Clasificar perfiles de tienda",
  "01c_recalculate_network_forecast": "1c. Recalcular forecast de red",
  "02_detect_stockouts": "2. Refrescar snapshots y detectar stockouts",
  "03_recalculate_forecasts": "3. Recalcular forecast por tienda",
  "03b_recalculate_rop": "3b. Recalcular ROP",
  "03c_refresh_recommendations": "3c. Refrescar recomendaciones",
  "04_replenishment_calculator": "4. Calcular reposición por tienda",
  "04b_recalculate_criticality": "4b. Recalcular criticidad",
  "04c_process_new_receipts": "4c. Procesar nuevos recibos",
  "05_refresh_metrics": "5. Refrescar métricas",
  "06_emit_completion_event": "6. Emitir evento de completado",
  // Steps legados (runs históricos, versión previa del orquestador)
  "04_recalculate_capacity": "4. Recalcular capacidad (legado)",
  "05_emit_completion_event": "5. Emitir evento de completación (legado)",
};

function formatDuration(ms: number | null): string {
  if (ms === null || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s - m * 60);
  return `${m}m ${rs}s`;
}

function formatRelativeTime(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

function statusIcon(status: PostSyncStepStatus): React.ReactElement {
  switch (status) {
    case "running":
      return <CircularProgress size={16} />;
    case "completed":
      return <CheckCircle fontSize="small" sx={{ color: "success.main" }} />;
    case "failed":
      return <ErrorIcon fontSize="small" sx={{ color: "error.main" }} />;
    case "skipped":
      return (
        <RemoveCircleOutline
          fontSize="small"
          sx={{ color: "warning.main" }}
        />
      );
    default:
      return <Pending fontSize="small" />;
  }
}

function statusChipColor(
  status: PipelineRunLog["overall_status"],
): "success" | "error" | "warning" | "info" | "default" {
  switch (status) {
    case "completed":
      return "success";
    case "failed":
      return "error";
    case "partial":
      return "warning";
    case "running":
      return "info";
    default:
      return "default";
  }
}

function StepRow({ step }: { step: PipelineStepLog }): React.ReactElement {
  const [expandError, setExpandError] = useState(false);
  const label = STEP_LABELS[step.step] ?? step.step;
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1.5}
      sx={{ py: 0.75 }}
    >
      <Box sx={{ mt: 0.25 }}>{statusIcon(step.status)}</Box>
      <Stack flex={1} spacing={0.25}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="body2" fontWeight={500}>
            {label}
          </Typography>
          <Chip
            label={step.status}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: "0.65rem" }}
          />
          <Typography variant="caption" color="text.secondary">
            {formatDuration(step.duration_ms)}
          </Typography>
          {step.status === "failed" && step.error_message && (
            <Tooltip title={expandError ? "Ocultar error" : "Ver error"}>
              <IconButton
                size="small"
                onClick={() => setExpandError((v) => !v)}
                sx={{ p: 0.25 }}
              >
                {expandError ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {step.status === "failed" && step.error_message && (
          <Collapse in={expandError}>
            <Alert
              severity="error"
              variant="outlined"
              sx={{ fontSize: "0.75rem", py: 0.5, mt: 0.5 }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  m: 0,
                }}
              >
                {step.error_message}
              </Typography>
            </Alert>
          </Collapse>
        )}
        {step.status === "skipped" && step.error_message && (
          <Typography variant="caption" color="warning.main">
            {step.error_message}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function LastRunCard({ run }: { run: PipelineRunLog }): React.ReactElement {
  const sortedSteps = useMemo(
    () =>
      [...run.steps].sort((a, b) =>
        a.started_at.localeCompare(b.started_at),
      ),
    [run.steps],
  );
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderColor: `${statusChipColor(run.overall_status)}.light` }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Último run
            </Typography>
            <Chip
              label={run.overall_status}
              size="small"
              color={statusChipColor(run.overall_status)}
            />
            <Typography variant="caption" color="text.secondary">
              {run.steps_completed}/{run.steps_total} completados ·{" "}
              {run.steps_failed} fallidos · {run.steps_skipped} skipped
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatRelativeTime(run.started_at)} ·{" "}
            {formatDuration(run.duration_ms)}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
        >
          sync_run_id: {run.sync_run_id}
        </Typography>
        <Divider />
        <Stack spacing={0.25}>
          {sortedSteps.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Sin steps registrados todavía.
            </Typography>
          ) : (
            sortedSteps.map((s) => <StepRow key={s.step} step={s} />)
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function HistoryTable({
  runs,
}: {
  runs: PipelineRunLog[];
}): React.ReactElement {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Estado</TableCell>
            <TableCell>sync_run_id</TableCell>
            <TableCell>Inicio</TableCell>
            <TableCell align="right">Duración</TableCell>
            <TableCell align="right">Steps</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((r) => (
            <TableRow key={r.sync_run_id} hover>
              <TableCell>
                <Chip
                  label={r.overall_status}
                  size="small"
                  color={statusChipColor(r.overall_status)}
                />
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                {r.sync_run_id}
              </TableCell>
              <TableCell>{formatRelativeTime(r.started_at)}</TableCell>
              <TableCell align="right">
                {formatDuration(r.duration_ms)}
              </TableCell>
              <TableCell align="right">
                {r.steps_completed}/{r.steps_total}
                {r.steps_failed > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="error.main"
                    sx={{ ml: 0.5 }}
                  >
                    ({r.steps_failed} ✗)
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function DistribucionPostSyncMonitor(): React.ReactElement {
  const { data, isLoading, isError, refetch } = useDistributionPostSyncRuns(10);
  const [historyOpen, setHistoryOpen] = useState(false);
  const lastRun = data?.[0];
  const olderRuns = data?.slice(1, 6) ?? [];

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <TimelineIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Monitor pipeline §7 (post-sync distribución)
            </Typography>
            {lastRun?.overall_status === "running" && (
              <Chip
                icon={<CircularProgress size={12} />}
                label="En ejecución"
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refrescar
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Lee <code>post_sync_run_log</code> con polling adaptativo (5s si hay
          run en ejecución, 30s en idle). Muestra los 9 steps del orquestador
          con su estado y errores en línea.
        </Typography>

        {isError && (
          <Alert severity="error">
            Error al cargar runs del orquestador. Refresca o revisa el backend.
          </Alert>
        )}

        {isLoading && !data && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2">Cargando runs…</Typography>
          </Stack>
        )}

        {!isLoading && data && data.length === 0 && (
          <Alert severity="info" icon={<HourglassEmpty />}>
            El orquestador nunca ha corrido. Dispáralo con el botón "Ejecutar
            recálculo de distribución" arriba.
          </Alert>
        )}

        {lastRun && <LastRunCard run={lastRun} />}

        {/* Terminal con tail del Logger (verbose, autoscroll, polling 2s) */}
        <Divider />
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" fontWeight="bold">
            Logs en vivo del backend
          </Typography>
          <TerminalLogTail pollingMs={2000} maxHeightPx={360} />
        </Stack>

        {olderRuns.length > 0 && (
          <>
            <Divider />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Runs anteriores ({olderRuns.length})
              </Typography>
              <IconButton
                size="small"
                onClick={() => setHistoryOpen((v) => !v)}
                aria-label={historyOpen ? "Ocultar histórico" : "Ver histórico"}
              >
                {historyOpen ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>
            <Collapse in={historyOpen}>
              <HistoryTable runs={olderRuns} />
            </Collapse>
          </>
        )}
      </Stack>
    </Paper>
  );
}
